import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getClients } from "../Api";
import { updateClient } from "../redux/filters/filters.actions";
import {
  accessorCanSelectClient,
  clientLogoFromApi,
  readSelectedClient,
  writeSelectedClient,
} from "../utils/selectedClientStorage";

const toSelectorClient = item => {
  if (!item?.uid) return null;
  const logo = clientLogoFromApi(item);
  return {
    value: item.uid,
    label: item.name || item.uid,
    ...(logo ? { logo } : {}),
  };
};

const validateStoredClient = async stored => {
  if (!stored?.value) return null;
  try {
    const { clients, count } = await getClients({
      search: stored.label || "",
      pageSize: 100,
    });
    const match = (clients || []).find(item => item.uid === stored.value);
    if (match) return toSelectorClient(match);
    if (!count) return null;
    return stored;
  } catch {
    return stored;
  }
};

export const hydrateSelectedClient = (dispatch, accessor) => {
  if (!accessorCanSelectClient(accessor) || !accessor?.uid) return;
  const stored = readSelectedClient(accessor.uid);
  if (stored?.value) dispatch(updateClient(stored));
};

const usePersistedClientSelection = () => {
  const dispatch = useDispatch();
  const accessor = useSelector(state => state.accessor.accessor);
  const client = useSelector(state => state.filters.client);
  const restoredForUid = useRef(null);
  const skipNextPersist = useRef(false);

  const canSelect = accessorCanSelectClient(accessor);
  const userUid = accessor?.uid || null;

  useEffect(() => {
    if (!userUid || !canSelect) {
      if (!userUid) dispatch(updateClient(null));
      restoredForUid.current = null;
      return undefined;
    }

    const stored = readSelectedClient(userUid);
    if (restoredForUid.current !== userUid) {
      restoredForUid.current = userUid;
      skipNextPersist.current = true;
      if (stored?.value) dispatch(updateClient(stored));
    }

    if (!stored?.value) return undefined;

    let cancelled = false;
    (async () => {
      const resolved = await validateStoredClient(stored);
      if (cancelled) return;
      if (!resolved) {
        dispatch(updateClient(null));
        return;
      }
      if (
        resolved.value !== stored.value ||
        resolved.label !== stored.label ||
        resolved.logo !== stored.logo
      ) {
        dispatch(updateClient(resolved));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userUid, canSelect, dispatch]);

  useEffect(() => {
    if (!userUid || !canSelect) return;
    if (restoredForUid.current !== userUid) return;
    if (skipNextPersist.current) {
      skipNextPersist.current = false;
      return;
    }
    writeSelectedClient(userUid, client);
  }, [userUid, canSelect, client]);
};

export default usePersistedClientSelection;
